import { apiClient, type Api } from "../../services/api";
import {
  gaEventPracticeProfileRailArticleClicked,
  gaEventPracticeProfileRailViewed,
  type PracticeRailCorpusProps,
} from "../../tools/analytics/domains/practice.analytics";
import { newEventId } from "../../tools/analytics/eventIdentity";
import type { PracticeRailCorpus } from "./hooks/usePracticeRailTerms";

/**
 * The practice rail's two reader-side facts, written to BOTH sinks by one call.
 *
 * The two sinks answer different questions and neither can be dropped. Mixpanel
 * is where the boards read the funnel; `user_content_events` is where the
 * per-surface interaction rate is computed, and it is the one that was empty —
 * the practice surface carried card impressions and no interaction of any kind,
 * so its rate measured the adoption of the rail and read as disengagement.
 *
 * One function per fact rather than one call site per sink, because the failure
 * this closes is precisely two sinks drifting apart: saves and shares still
 * leave this surface through GA4 alone.
 */

/** Inferred from the route, never redeclared (`impressionBatcher` precedent). */
type RailEventBody =
  Api["post"]["/webapp/private/practice-profiles/rail-events"]["input"]["json"];

/**
 * The corpus, resolved ONCE into everything the two sinks and the impression key
 * need. One switch and not three: the GA props, the column value and the
 * fire-once key are three readings of the same discriminant.
 */
function railCorpus(corpus: PracticeRailCorpus): {
  props: PracticeRailCorpusProps;
  /** `null` is the aggregate — a union of the reader's practices, not one. */
  practiceProfileUid: string | null;
  key: string;
} {
  switch (corpus.kind) {
    case "all":
      return {
        props: { corpus: "all" },
        practiceProfileUid: null,
        key: "all",
      };
    case "practice":
      return {
        props: { corpus: "practice", practice_profile_uid: corpus.uid },
        practiceProfileUid: corpus.uid,
        key: `practice:${corpus.uid}`,
      };
    default:
      throw new Error(corpus satisfies never);
  }
}

/**
 * The column value for the corpus, for the OTHER writer of this key: the feed's
 * viewport impressions, which land through the recommender's batcher and not
 * through `recordRailEvent`. Exported so the mapping keeps one owner — a second
 * `corpus.kind === "all" ? null : corpus.uid` next door is how the two halves of
 * one funnel drift apart.
 */
export function practiceRailProfileUid(
  corpus: PracticeRailCorpus
): string | null {
  return railCorpus(corpus).practiceProfileUid;
}

/**
 * What `useTrackOnMount` counts one view per, per SESSION. Switching practice is
 * a new view; tapping a MeSH chip is not, because a chip narrows the rail rather
 * than replacing it, and neither is coming back from an article.
 */
export function practiceRailCorpusKey(corpus: PracticeRailCorpus): string {
  return railCorpus(corpus).key;
}

/**
 * Fire-and-forget, like the Room suggestion events: a failure reaches Sentry
 * through the api client's global `onRequestError`, and a lost telemetry row is
 * never worth a toast.
 *
 * `sourceEventId` is a fresh uuid per CALL for the open, because a second tap
 * is a second open. The rail VIEW derives its id from the corpus and the
 * session instead, so the `@unique` column enforces the count the client is
 * already keeping and a replayed request cannot inflate the denominator.
 */
function recordRailEvent(body: RailEventBody): void {
  void apiClient.post("/webapp/private/practice-profiles/rail-events", {
    json: body,
  });
}

/**
 * The rail reached the screen, for this corpus.
 *
 * `sourceEventId` comes from the caller because `useTrackOnMount` is what
 * counted this view: the id it derived and the fact it claimed are the same
 * string, which re-deriving it here would quietly stop being.
 */
export function recordPracticeRailViewed(
  corpus: PracticeRailCorpus,
  sourceEventId: string
): void {
  const { props, practiceProfileUid } = railCorpus(corpus);
  gaEventPracticeProfileRailViewed(props);
  recordRailEvent({ kind: "rail_viewed", practiceProfileUid, sourceEventId });
}

/** A reader opened a publication from the rail. */
export function recordPracticeRailArticleClicked(
  corpus: PracticeRailCorpus,
  articleId: string
): void {
  const { props, practiceProfileUid } = railCorpus(corpus);
  gaEventPracticeProfileRailArticleClicked({ ...props, articleId });
  recordRailEvent({
    kind: "article_clicked",
    practiceProfileUid,
    articleId,
    sourceEventId: newEventId(),
  });
}
